import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { RestfulEndpoint } from './restful-endpoint.service';

export type UserActivityExportSource = 'summary_pdf' | 'build_report';

export type ObservationExportFormat =
  | 'excel_with_images'
  | 'excel_without_images'
  | 'csv'
  | 'summary_pdf';

/**
 * Optional audit details of one export. The backend reads them leniently and
 * ignores them when they are missing, so callers may send any subset.
 */
export interface UserActivityExportDetails {
  export_id?: string;
  export_format?: ObservationExportFormat;
  total_rows?: number;
  filters?: Record<string, unknown>;
}

/**
 * Makes one id per export press, so the backend records one audit row for an export
 * however many requests it takes. Returns undefined where the browser has no
 * `crypto.randomUUID` (insecure context); the backend then makes the id itself.
 */
export function createExportId(): string | undefined {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    return undefined;
  }

  return crypto.randomUUID();
}

@Injectable({
  providedIn: 'root',
})
export class UserActivityService {
  constructor(private endPoint: RestfulEndpoint) {}

  /**
   * Records that the user triggered an export. Failures are swallowed so tracking
   * can never interrupt the export the user actually asked for.
   */
  trackExport(
    projectId: string,
    source: UserActivityExportSource,
    details?: UserActivityExportDetails,
  ): void {
    if (!projectId) {
      return;
    }

    this.endPoint
      .post(
        'v2/user-activity/exports',
        { ...details, project_id: projectId, source },
        new HttpHeaders({ 'X-Skip-Loading-Bar': 'true' }),
      )
      .subscribe({
        error: () => {},
      });
  }

  /**
   * Records why a PDF export/print failed so support can follow up. Like trackExport, it must
   * never surface an error of its own.
   */
  trackExportFailure(
    projectId: string,
    reportId: string,
    action: 'download' | 'print',
    reason: string,
  ): void {
    if (!projectId) {
      return;
    }

    this.endPoint
      .post(
        'v2/user-activity/export-failures',
        { project_id: projectId, report_id: reportId || null, action, reason },
        new HttpHeaders({ 'X-Skip-Loading-Bar': 'true' }),
      )
      .subscribe({
        error: () => {},
      });
  }
}
